import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Lock, CheckCircle2, Copy, Sparkles, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader } from "@/components/common";
import { GoogleDriveVideoPlayer } from "@/components/GoogleDriveVideoPlayer";
import { copyText } from "@/pages/landing/Checkout";
import { ChallengeShell, StepBar, fmtUnlock } from "./ChallengeShell";
import { Question, isVisible } from "./Questions";
import { IdeaBrief } from "./IdeaBrief";

function CtaCard({ cta }) {
  useEffect(() => { if (cta) api.post("/challenge/cta", { cta_id: cta.id, action: "view" }).catch(() => {}); }, [cta?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cta) return null;
  return (
    <aside className="rounded-3xl border-2 border-[#13283A] bg-white p-5" data-testid="challenge-cta-card">
      <div className="text-xs font-extrabold text-[#13B698]">OFFRE FACULTATIVE</div>
      <div className="font-display font-extrabold text-lg text-[#13283A] mt-1" data-testid="challenge-cta-screen">{cta.screen}</div>
      <p className="text-sm text-[#36495A] mt-1">{cta.text}</p>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <Link to={cta.link} onClick={() => api.post("/challenge/cta", { cta_id: cta.id, action: "click" }).catch(() => {})} className="min-h-[48px] px-5 rounded-2xl bg-[#13283A] text-white font-bold inline-flex items-center gap-2" data-testid="challenge-cta-btn">{cta.button} <ArrowRight className="h-4 w-4" /></Link>
        <span className="text-sm text-[#5C6B73]" data-testid="challenge-cta-free">ou {cta.free.toLowerCase()}</span>
      </div>
    </aside>
  );
}

function Locked({ info }) {
  return (
    <div className="rounded-3xl bg-white border border-[#E1ECEF] p-6 text-center" data-testid="challenge-day-locked">
      <Lock className="h-8 w-8 mx-auto text-[#7D8C96]" />
      <h1 className="font-display font-extrabold text-xl text-[#13283A] mt-3">Cette étape n’est pas encore ouverte</h1>
      <p className="text-sm text-[#5C6B73] mt-2">{info?.unlock_at ? `Elle s’ouvrira le ${fmtUnlock(info.unlock_at)}.` : "Termine d’abord l’étape précédente."}</p>
      <Link to="/dashboard/challenge" className="mt-4 inline-flex min-h-[48px] px-5 rounded-2xl bg-[#13B698] text-white font-bold items-center" data-testid="challenge-locked-back">Retour à mon challenge</Link>
    </div>
  );
}

function Draft() {
  const [t, setT] = useState("");
  const [busy, setBusy] = useState(false);
  const gen = async () => { setBusy(true); try { setT((await api.post("/challenge/day/4/draft")).data.text); } catch { toast.error("Réessaie dans un instant."); } finally { setBusy(false); } };
  return (
    <div className="rounded-2xl bg-[#F4F8F9] p-4" data-testid="outreach-draft-box">
      <div className="font-display font-bold text-[#13283A]">Ton message à copier</div>
      <p className="text-xs text-[#5C6B73] mt-1">Sidebizz n’envoie aucun message à ta place. Copie-le et envoie-le toi-même, si tu le souhaites.</p>
      {t && <p className="mt-3 rounded-xl bg-white p-3 text-[15px] whitespace-pre-wrap" data-testid="outreach-draft-text">{t}</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        <button type="button" disabled={busy} onClick={gen} className="min-h-[44px] px-4 rounded-xl bg-[#13283A] text-white text-sm font-bold inline-flex items-center gap-2" data-testid="outreach-draft-generate"><Sparkles className="h-4 w-4" />{busy ? "Préparation…" : t ? "Nouvelle version" : "Préparer mon message"}</button>
        {t && <button type="button" onClick={async () => ((await copyText(t)) ? toast.success("Message copié") : toast.error("Copie impossible, copie-le à la main."))} className="min-h-[44px] px-4 rounded-xl border border-[#D5E3E8] bg-white text-sm font-bold inline-flex items-center gap-2" data-testid="outreach-draft-copy"><Copy className="h-4 w-4" />Copier</button>}
      </div>
    </div>
  );
}

function Results({ d }) {
  const o = d.outputs || {};
  return (
    <div className="space-y-3" data-testid="challenge-day-results">
      {d.day === 1 && d.recommendations.length > 0 && (
        <div className="space-y-2" data-testid="challenge-recommendations">
          <h2 className="font-display font-bold text-[#13283A]">Tes 3 idées à comparer</h2>
          {d.warning && <p className="text-sm rounded-xl bg-amber-50 border border-amber-200 p-3 text-[#5C4A1A]" data-testid="challenge-rec-warning">{d.warning}</p>}
          {d.recommendations.map((r) => <div key={r.number} className="rounded-2xl bg-white border border-[#E1ECEF] p-4" data-testid={`challenge-rec-${r.number}`}><div className="flex items-center gap-2 font-display font-bold text-[#13283A]"><Lightbulb className="h-5 w-5 text-[#13B698]" />{r.name}</div><ul className="mt-2 text-sm text-[#36495A] space-y-0.5">{r.reasons.map((x) => <li key={x}>• {x}</li>)}</ul></div>)}
        </div>
      )}
      {d.day === 2 && o.idea_rationale && <div className="rounded-2xl bg-white border border-[#E1ECEF] p-4 text-[15px] whitespace-pre-wrap" data-testid="challenge-rationale">{o.idea_rationale}</div>}
      {d.day === 3 && o.offer_statement && <div className="rounded-2xl bg-[#E8F8F4] border border-[#13B698] p-4" data-testid="challenge-offer-statement"><div className="text-xs font-bold text-[#13283A]">MA PREMIÈRE OFFRE (version 1)</div><p className="text-[15px] mt-1 whitespace-pre-wrap">{o.offer_statement}</p></div>}
      {d.day === 4 && d.validation && <div className="rounded-2xl bg-white border border-[#E1ECEF] p-4 text-[15px]" data-testid="challenge-validation"><b>Où en es-tu ?</b> {d.validation}</div>}
      {d.day === 5 && d.brief && <IdeaBrief brief={d.brief} tier={d.tier} />}
    </div>
  );
}

export default function ChallengeDay() {
  const { day } = useParams();
  const n = Number(day);
  const [d, setD] = useState(null);
  const [locked, setLocked] = useState(null);
  const [a, setA] = useState({});
  const [extra, setExtra] = useState([]);
  const [busy, setBusy] = useState(false);
  const pending = useRef({});
  const timer = useRef(null);
  const load = () => api.get(`/challenge/day/${n}`).then((r) => { setD(r.data); setA(r.data.answers || {}); setLocked(null); }).catch((e) => {
    if (e?.response?.status === 403) { try { setLocked(JSON.parse(e.response.data.detail)); } catch { setLocked({}); } } else if (e?.response?.status === 404) window.location.replace("/dashboard/challenge");
  });
  useEffect(() => { setD(null); api.post("/challenge/enroll").finally(load); window.scrollTo(0, 0); }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  const flush = async () => {
    clearTimeout(timer.current);
    const body = pending.current;
    pending.current = {};
    if (Object.keys(body).length) await api.put(`/challenge/day/${n}/answers`, { answers: body }).catch((e) => toast.error(e?.response?.data?.detail || "Réponse non sauvegardée"));
  };
  const change = (k, v) => { setA((x) => ({ ...x, [k]: v })); pending.current[k] = v; clearTimeout(timer.current); timer.current = setTimeout(flush, 700); };
  useEffect(() => () => clearTimeout(timer.current), []);
  const complete = async () => {
    setBusy(true);
    try { await flush(); await api.post(`/challenge/day/${n}/complete`); toast.success("Étape terminée !"); await load(); setTimeout(() => document.getElementById("challenge-after")?.scrollIntoView({ behavior: "smooth" }), 100); }
    catch (e) { toast.error(e?.response?.data?.detail || "Réessaie dans un instant."); } finally { setBusy(false); }
  };
  if (locked) return <ChallengeShell><Locked info={locked} /></ChallengeShell>;
  if (!d) return <Loader />;
  const done = d.status.state === "completed";
  const nxt = d.days.find((x) => x.day === n + 1);
  return (
    <ChallengeShell>
      <article className="space-y-5" data-testid="challenge-day-page" lang="fr">
        <div><StepBar days={d.days} current={n} /></div>
        <header>
          <div className="text-xs font-extrabold text-[#13B698]" data-testid="challenge-day-number">JOUR {n} / 5</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#13283A] leading-tight mt-1" data-testid="challenge-day-title">{d.title}</h1>
        </header>
        {d.video ? <GoogleDriveVideoPlayer fileId={d.video.file_id} title={`Vidéo du Jour ${n} : ${d.video.title}`} /> : <GoogleDriveVideoPlayer fileId="" title="" />}
        <section className="rounded-3xl bg-white border border-[#E1ECEF] p-5 space-y-3 text-[15px] text-[#36495A] leading-relaxed" data-testid="challenge-lesson-text">
          <p className="font-semibold text-[#13283A]">{d.hook}</p>
          <p>{d.teaching}</p>
          <p className="rounded-2xl bg-[#F4F8F9] p-3"><b className="text-[#13283A]">Exemple à Maurice : </b>{d.example}</p>
        </section>
        <section className="rounded-3xl bg-white border border-[#E1ECEF] p-5 space-y-6" data-testid="challenge-exercise">
          <h2 className="font-display font-extrabold text-xl text-[#13283A]">Ton exercice du jour</h2>
          {n === 4 && <Draft />}
          {d.questions.filter((q) => isVisible(q, a)).map((q) => <Question key={q.key} q={q} value={a[q.key]} onChange={(v) => change(q.key, v)} canBrowse={d.can_browse_catalogue} extraOptions={extra} onExtra={(o) => setExtra((x) => [...x, o])} />)}
          <p className="text-xs text-[#7D8C96]">Tes réponses sont sauvegardées automatiquement. Tu peux les modifier plus tard.</p>
          <button disabled={busy} onClick={complete} className="w-full min-h-[56px] rounded-2xl bg-[#13B698] text-white font-extrabold text-[16px] inline-flex items-center justify-center gap-2" data-testid="challenge-complete-btn">{busy ? "Enregistrement…" : done ? "Mettre à jour mon étape" : "Terminer l’étape"} <CheckCircle2 className="h-5 w-5" /></button>
        </section>
        {done && (
          <div id="challenge-after" className="space-y-4">
            <Results d={d} />
            <div className="rounded-3xl bg-[#13283A] text-white p-5" data-testid="challenge-next-step">
              <div className="font-display font-bold flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[#13B698]" />Jour {n} terminé</div>
              {n < 5 && nxt && (nxt.state === "available"
                ? <Link to={`/challenge/day/${n + 1}`} className="mt-3 w-full sm:w-auto min-h-[52px] px-6 rounded-2xl bg-[#13B698] font-extrabold inline-flex items-center justify-center gap-2" data-testid="challenge-next-day-btn">Passer au Jour {n + 1} <ArrowRight className="h-5 w-5" /></Link>
                : <p className="text-sm text-white/80 mt-2" data-testid="challenge-next-unlock">Le Jour {n + 1} s’ouvrira le {nxt.unlock_at ? fmtUnlock(nxt.unlock_at) : "bientôt"}. {d.email_opt_out ? "" : "On t’envoie un email quand il est prêt."}</p>)}
              {n === 5 && <Link to="/dashboard/challenge" className="mt-3 inline-flex min-h-[48px] items-center font-bold text-[#13B698]" data-testid="challenge-finish-link">Voir mon tableau de bord →</Link>}
            </div>
            <CtaCard cta={d.cta} />
          </div>
        )}
      </article>
    </ChallengeShell>
  );
}
